/**
  © BlockyApps. You are permitted to use this content within your store. Redistribution or use in any other application is strictly prohibited.
  Unauthorized copying, distribution, or reproduction in any form will result in legal action.
**/

if (!customElements.get("blocky-product-slider")) {
  customElements.define(
    "blocky-product-slider",
    class BlockyProductSlider extends HTMLElement {
      constructor() {
        super();

        this.pendingSteps = 0;

        this.handleClick = this.handleClick.bind(this);
        this.handleRunBefore = this.handleRunBefore.bind(this);
        this.handleRunAfter = this.handleRunAfter.bind(this);
        this.runQueuedStep = this.runQueuedStep.bind(this);
      }

      connectedCallback() {
        if (this.sliderReady) return;

        this.glideEl = this.querySelector(".product-slider-glide");
        if (!this.glideEl) return;

        this.slides = Array.from(this.glideEl.querySelectorAll(".glide__slide"));
        if (!this.slides.length || typeof Glide === "undefined") return;

        this.slideCount = this.slides.length;
        this.videoAutoplay = this.getAttribute("data-video-autoplay") === "true";
        this.isMuted = this.getAttribute("data-video-muted") !== "false";
        this.videoLoop = this.getAttribute("data-video-loop") === "true";
        this.pendingSteps = 0;
        this.sliderReady = true;
        this.assignSlideIndexes();

        try {
          this.glide = new Glide(this.glideEl, this.getOptions());
          this.glide.on("run.before", this.handleRunBefore);
          this.glide.on("run.after", this.handleRunAfter);
          this.glide.mount();
        } catch (e) {
          this.sliderReady = false;
          this.glide = null;
          return;
        }

        this.glideEl.addEventListener("click", this.handleClick);
        this.setVisualActive(this.glide.index);
        this.syncMedia();
      }

      disconnectedCallback() {
        this.destroy();
      }

      assignSlideIndexes() {
        this.slides.forEach(function (slideEl, index) {
          slideEl.setAttribute("data-slide-index", index);
        });
      }

      // Shopify `number` settings are free-text boxes, so any of these attributes
      // can arrive as something parseInt turns into NaN. NaN then spreads into
      // perView / gap / animationDuration and Glide sizes every slide to "NaNpx",
      // which collapses the whole track. Fall back instead of propagating it.
      readInt(el, attribute, fallback) {
        var parsed = parseInt(el.getAttribute(attribute), 10);
        return isFinite(parsed) ? parsed : fallback;
      }

      getOptions() {
        var requested = this.readInt(this.glideEl, "data-slides-per-view", 5);
        var requestedMobile = this.readInt(this.glideEl, "data-slides-per-view-mobile", 2);
        var animationMs = this.readInt(this, "data-animation-ms", 500);
        var peekDesktopBefore = this.readInt(this.glideEl, "data-peek-desktop-before", 40);
        var peekDesktopAfter = this.readInt(this.glideEl, "data-peek-desktop-after", 40);
        var peekMobileBefore = this.readInt(this.glideEl, "data-peek-mobile-before", 30);
        var peekMobileAfter = this.readInt(this.glideEl, "data-peek-mobile-after", 30);
        // `parseInt(...) || 16` turned a deliberate gap of 0 back into 16, because
        // 0 is falsy. Only fall back when the value is genuinely unparseable.
        var gapValue = parseInt(getComputedStyle(this).getPropertyValue("--ps-gap"), 10);
        if (!isFinite(gapValue)) gapValue = 16;
        var autoplaySpeed = this.readInt(this.glideEl, "data-autoplay-speed", 4000);
        // Optional. Falls back to the desktop gap, so sections that don't set it
        // behave exactly as before.
        var gapMobile = this.readInt(this.glideEl, "data-gap-mobile", gapValue);
        var cap = function (value) {
          return Math.max(1, Math.min(value, this.slideCount));
        }.bind(this);

        // Glide's carousel only clones as many slides as it actually has, so asking
        // for more per view than exist leaves gaps instead of wrapping seamlessly.
        var perView = cap(requested);
        var perViewMobile = cap(requestedMobile);

        return {
          type: "carousel",
          focusAt: "center",
          perView: perView,
          startAt: Math.floor(this.slideCount / 2),
          gap: gapValue,
          animationDuration: Math.max(200, animationMs),
          dragThreshold: 100,
          swipeThreshold: 100,
          peek: { before: peekDesktopBefore, after: peekDesktopAfter },
          autoplay: this.glideEl.getAttribute("data-autoplay") === "true" ? autoplaySpeed : false,
          breakpoints: {
            750: {
              perView: perViewMobile,
              peek: { before: peekMobileBefore, after: peekMobileAfter },
              gap: gapMobile,
            },
          },
        };
      }

      clampIndex(index) {
        if (this.slideCount <= 0) return 0;
        return ((index % this.slideCount) + this.slideCount) % this.slideCount;
      }

      getSlideVideo(slideEl) {
        return slideEl ? slideEl.querySelector("video.product-slider-slide__video") : null;
      }

      getRenderedSlides() {
        return Array.from(this.glideEl.querySelectorAll(".glide__slide[data-slide-index]"));
      }

      getTrackSlides() {
        return Array.from(this.glideEl.querySelectorAll(".glide__slides > li"));
      }

      getOriginalSlides() {
        return Array.from(this.glideEl.querySelectorAll(".glide__slide:not(.glide__slide--clone)"));
      }

      getCenteredSlide() {
        return (
          this.glideEl.querySelector(".glide__slide--active") ||
          this.getOriginalSlides()[this.clampIndex(this.glide ? this.glide.index : 0)] ||
          null
        );
      }

      // Measured across the rendered track, clones included, so clicking one of the
      // peeking edge slides steps the short way round instead of rewinding the strip.
      getStepsTo(slideEl) {
        var rendered = this.getTrackSlides();
        var from = rendered.indexOf(this.getCenteredSlide());
        var to = rendered.indexOf(slideEl);
        if (from < 0 || to < 0) return 0;
        return to - from;
      }

      queueSteps(steps) {
        this.pendingSteps = steps;
        this.runQueuedStep();
      }

      // One direction-only hop at a time: '>' and '<' are the only moves Glide wraps
      // at the ends, so the loop stays endless in both directions.
      runQueuedStep() {
        if (!this.glide || !this.pendingSteps) return;

        var forward = this.pendingSteps > 0;
        this.pendingSteps += forward ? -1 : 1;
        this.glide.go(forward ? ">" : "<");
      }

      getCurrentActiveSlide() {
        var currentIndex = this.clampIndex(this.glide ? this.glide.index : 0);
        return (
          this.glideEl.querySelector(
            '.glide__slide--active[data-slide-index="' + currentIndex + '"]'
          ) || this.glideEl.querySelector('.glide__slide[data-slide-index="' + currentIndex + '"]')
        );
      }

      applySoundButtonClass(buttonEl) {
        if (!buttonEl) return;

        if (this.isMuted) buttonEl.classList.remove("is-sound-on");
        else buttonEl.classList.add("is-sound-on");
      }

      syncSoundButtons() {
        this.querySelectorAll("[data-sound-toggle]").forEach(
          function (buttonEl) {
            this.applySoundButtonClass(buttonEl);
          }.bind(this)
        );
      }

      setVisualActive(activeIndex) {
        var normalizedIndex = this.clampIndex(activeIndex);

        this.getRenderedSlides().forEach(function (slideEl) {
          var slideIndex = parseInt(slideEl.getAttribute("data-slide-index") || "-1", 10);
          if (slideIndex === normalizedIndex) slideEl.classList.add("is-current");
          else slideEl.classList.remove("is-current");
        });
      }

      pauseInactiveVideos(activeSlide) {
        this.getRenderedSlides().forEach(
          function (slideEl) {
            var videoEl = this.getSlideVideo(slideEl);
            if (!videoEl || slideEl === activeSlide) return;

            try {
              videoEl.pause();
              videoEl.muted = true;
            } catch (e) {}
          }.bind(this)
        );
      }

      pauseAllVideos() {
        this.pauseInactiveVideos(null);
      }

      async playActiveVideo(activeSlide) {
        var videoEl = this.getSlideVideo(activeSlide);
        if (!videoEl) return;

        videoEl.loop = !!this.videoLoop;
        videoEl.muted = !!this.isMuted;

        if (!this.videoAutoplay) return;

        try {
          var playResult = videoEl.play();
          if (playResult && typeof playResult.then === "function") {
            await playResult;
          }
        } catch (e) {
          // Ignore autoplay rejection
        }
      }

      syncMedia() {
        var activeSlide = this.getCurrentActiveSlide();

        this.syncSoundButtons();
        this.pauseInactiveVideos(activeSlide);

        if (!activeSlide) return;

        this.playActiveVideo(activeSlide);
      }

      toggleMute() {
        this.isMuted = !this.isMuted;
        this.syncSoundButtons();
        return this.isMuted;
      }

      getTargetIndex(move) {
        if (!move || !this.glide) return 0;

        var direction = move.direction;
        var rawSteps = move.steps;
        var steps = parseInt(rawSteps, 10) || 1;

        if (direction === "=") return this.clampIndex(steps);
        if (direction === ">") return this.clampIndex(this.glide.index + steps);
        if (direction === "<") return this.clampIndex(this.glide.index - steps);
        return this.clampIndex(this.glide.index);
      }

      handleRunBefore(move) {
        this.setVisualActive(this.getTargetIndex(move));
      }

      handleRunAfter() {
        this.setVisualActive(this.glide.index);
        this.syncMedia();

        if (this.pendingSteps) setTimeout(this.runQueuedStep, 0);
      }

      handleClick(event) {
        var slideEl = event.target.closest(".glide__slide");
        if (!slideEl || !this.glideEl.contains(slideEl) || !this.glide) return;

        if (event.target.closest("[data-sound-toggle]")) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleMute();
        }

        // A queue still draining means the centred slide is mid-flight, so the step
        // count read off it would be stale.
        if (this.pendingSteps) return;

        var steps = this.getStepsTo(slideEl);
        if (!steps) {
          this.syncMedia();
          return;
        }

        this.queueSteps(steps);
      }

      destroy() {
        if (this.glideEl) {
          this.glideEl.removeEventListener("click", this.handleClick);
        }

        this.pauseAllVideos();
        this.pendingSteps = 0;

        if (this.glide) {
          this.glide.destroy();
          this.glide = null;
        }

        this.sliderReady = false;
      }
    }
  );
}
